// @flow
import { createSelector } from 'reselect';
import { getProfile } from 'data/profile/selectors';
import { v4 as uuidv4 } from 'uuid';
import { getDatasetId } from 'utils/auth/selectors';

import { makeDataset, REDUCER_KEY } from './types';
import type { DatasetsStore } from './types';

export const getDatasetsStore = (state: any): DatasetsStore => state[REDUCER_KEY];

export const getDatasetsById = (state: any) => getDatasetsStore(state).resourcesById;
export const getLoadPending = (state: any) => getDatasetsStore(state).loadPending;
export const isLoading = (state: any) => getDatasetsStore(state).isLoading;
export const getError = (state: any) => getDatasetsStore(state).error;
export const getIsUpserting = (state: any) => getDatasetsStore(state).isUpserting;
export const getLastSyncDate = (state: any) => getDatasetsStore(state).lastSyncDate;

// Datasets sorted by name only
export const getDatasets = createSelector(
  getDatasetsById,
  (datasetsById) => !datasetsById ? null :
    datasetsById.sort((a, b) => (
      a.name.localeCompare(
        b.name,
        undefined,
        { numeric: true, sensitivity: 'base' }
      )
    ))
);

// Datasets sorted by most recently used, and then name
export const getSortedDatasets = createSelector(
  getDatasetsById,
  (datasetsById) => (!datasetsById ? null :
    datasetsById.sort((a, b) => new Date(a.lastSyncedAt) - new Date(b.lastSyncedAt))
  )
);

export const makeNewDataset = createSelector(
  getDatasetsById,
  getProfile,
  (datasetsById, profile) => {
    let name;
    const namePrototype = (profile && profile.firstName && `${profile.firstName}${'\''}s Finances`) || 'Finances';
    let counter = 0;
    do {
      name = counter ? `${namePrototype} ${counter}` : namePrototype;
      counter += 1;
      // eslint-disable-next-line
    } while (datasetsById && datasetsById.find((dataset) => dataset.name === name));
    const zipcode = (profile && profile.primaryAddress && profile.primaryAddress.zipCode) || '00000';
    const clientId = uuidv4().toUpperCase();

    return makeDataset({
      clientId,
      name,
      zipcode, // need to remove it if QCS aggree to make it optional (Mint legacy parameter)
    });
  },
);

export const getCurrentDataset = createSelector(
  getDatasetId,
  getDatasetsById,
  (datasetId, datasets) => datasets && datasets.get(datasetId)
);

export const isCanadianDataset = createSelector(
  (state, datasetId) => datasetId ? getDatasetsById(state).get(datasetId) : getCurrentDataset(state),
  (dataset) => ['QWIN_CA', 'QWIN_CA_MWF'].includes(dataset?.platform),
)

export const maybeCanadianDataset = createSelector(
  (state, datasetId) => datasetId ? getDatasetsById(state).get(datasetId) : getCurrentDataset(state),
  (dataset) => ['QWIN_CA', 'QWIN_CA_MWF', 'QMAC_MWF', 'QMAC'].includes(dataset?.platform),
)

export const getIsWebfirstDataset = createSelector(
  getCurrentDataset,
  (currentDataset) => currentDataset && currentDataset.platform &&
    ['QWIN_MWF', 'QMAC_MWF'].includes(currentDataset.platform),
);

export const getIsMacDataset = createSelector(
  getCurrentDataset,
  (currentDataset) => currentDataset?.platform.includes('QMAC'),
);
